// src/components/HeroSection.js

import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa';
import HeroImage from '../../assets/images/hero.jpg';
import { media } from '../../utils/media';

const HeroSection = ({ isAuthenticated }) => {
  return (
    <Section>
      <Overlay />
      <Container>
        <ContentWrapper>
          <TextContent>
            <TitleWrapper>
              <Title>
                ALL YOUR MOMENTS, <SubTitle>PERFECTLY PRESERVED</SubTitle>
              </Title>
              <H2Title>Your best sports memories, at your fingertips.</H2Title>
            </TitleWrapper>
            <ButtonGroup>
              {isAuthenticated ? (
                <HeroButton to="/dashboard"> 
                  Go to Dashboard
                  <ButtonIcon>
                    <FaArrowRight />
                  </ButtonIcon>
                </HeroButton>
              ) : (
                <HeroButton to="/explore">
                  Explore Courts
                  <ButtonIcon>
                    <FaArrowRight />
                  </ButtonIcon>
                </HeroButton>
              )}
            </ButtonGroup>
          </TextContent>
        </ContentWrapper>
      </Container>
      <ScrollIndicator>
        <ScrollText>Scroll Down</ScrollText>
        <ScrollArrow />
      </ScrollIndicator>
    </Section>
  );
};

export default HeroSection;
/* ────────────── STYLED COMPONENTS ────────────── */

/* 
   1) Section covers full viewport height with a parallax or 
      scrollable background depending on screen size.
   2) Uses your brand's 'HeroImage' with a dark gradient overlay.
*/
const Section = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  overflow: hidden;
  background: url(${HeroImage}) no-repeat center center;
  background-size: cover;
  background-attachment: fixed;
  
  /* Additional dark overlay below is replaced by <Overlay> for simpler layering */
  ${media.tablet(`
    background-attachment: scroll;
    min-height: 80vh;
  `)}
  ${media.mobile(`
    min-height: 70vh;
  `)}
`;

/* 
   A single overlay with a subtle gradient 
   that darkens the hero for improved text contrast.
*/
const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.85) 0%,
    rgba(0, 0, 0, 0.5) 100%
  );
  z-index: 1;
`;

const Container = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.lg};

  ${media.tablet(`
    padding: 0 ${({ theme }) => theme.spacing.md};
  `)}
  ${media.mobile(`
    padding: 0 ${({ theme }) => theme.spacing.md};
  `)}
`;

const ContentWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xl};
  animation: fadeInUp 1s ease-out forwards;
  position: relative;
  padding: ${({ theme }) => theme.spacing.lg};
  background: rgba(0, 0, 0, 0.25);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.1);

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  ${media.mobile(`
    width: 100%;
    margin: 0 auto;
    text-align: center;
    align-items: center;
    padding: ${({ theme }) => theme.spacing.md};
  `)}
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

/* 
   H1 with fallback clamp sizes. We do 
   some styling that suits large screens and shrinks on mobile.
*/
const Title = styled.h1`
  font-size: clamp(2.6rem, 4.5vw, 4rem);
  font-weight: 800;
  line-height: 1.1;
  color: ${({ theme }) => theme.colors.white};
  margin: 0;
  text-shadow: 0 3px 6px rgba(0, 0, 0, 0.3);
`;

/* SubTitle is a small inline block appended to the main Title text */
const SubTitle = styled.span`
  display: block;
  font-size: clamp(1.2rem, 2vw, 1.4rem);
  font-weight: 400;
  margin-top: 0.3em;
  opacity: 0.9;
`;

const H2Title = styled.h2`
  font-size: clamp(1.1rem, 2vw, 1.4rem);
  color: ${({ theme }) => theme.colors.white};
  line-height: 1.4;
  margin: 0;
  opacity: 0.85;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};

  ${media.mobile(`
    flex-direction: column;
    width: 100%;
    align-items: center;
  `)}
`;

/* 
   Gradient button using brand colors:
   Black (#010101) to your brand red (#BD2026).
   You could also use teal (#00a693) to orange (#ff6f3c)
   if you prefer a more vibrant accent.
*/
const HeroButton = styled(Link)`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.primary} 0%,
    ${({ theme }) => theme.colors.secondary} 100%
  );
  color: ${({ theme }) => theme.colors.white};
  font-size: 1.1rem;
  font-weight: 700;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  min-width: 180px;
  justify-content: center;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(189, 32, 38, 0.5);
  }

  &:active {
    transform: translateY(0);
  }

  ${media.mobile(`
    width: 100%;
    justify-content: center;
  `)}
`;

const ButtonIcon = styled.span`
  display: flex;
  align-items: center;
  transition: transform 0.3s ease;

  ${HeroButton}:hover & {
    transform: translateX(4px);
  }
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  z-index: 3;
  opacity: 0;
  animation: fadeIn 1.5s forwards 1s; /* delay for readability */

  @keyframes fadeIn {
    0%   { opacity: 0; transform: translate(-50%, 20px); }
    100% { opacity: 1; transform: translate(-50%, 0); }
  }
`;

const ScrollText = styled.span`
  color: ${({ theme }) => theme.colors.white};
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

const ScrollArrow = styled.div`
  width: 20px;
  height: 20px;
  border-right: 2px solid ${({ theme }) => theme.colors.white};
  border-bottom: 2px solid ${({ theme }) => theme.colors.white};
  transform: rotate(45deg);
  animation: bounce 2s infinite;

  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% {
      transform: translateY(0) rotate(45deg);
    }
    40% {
      transform: translateY(-10px) rotate(45deg);
    }
    60% {
      transform: translateY(-5px) rotate(45deg);
    }
  }
`;

